import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import track from "@/lib/track";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ArrowRight, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TrustBadges } from "@/components/TrustBadges";

export const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export const Eyebrow = ({ children }) => <p className="text-xs font-semibold tracking-widest uppercase text-blue-600">{children}</p>;

export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    if (description) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description);
    }
  }, [title, description]);
};

export const PageHeader = ({ eyebrow, title, intro }) => (
  <div className="pt-32 pb-4 md:pt-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl">{title}</h1>
      {intro && <p className="mt-5 max-w-2xl text-lg text-slate-600 leading-relaxed">{intro}</p>}
    </div>
  </div>
);

const NAV = [
  { to: "/who-we-serve", label: "Who We Serve" },
  { to: "/approach", label: "Our Approach" },
  { to: "/engagements", label: "How Engagements Work" },
  { to: "/deliverables", label: "Outcomes" },
  { to: "/trust", label: "Trust & Validation" },
];

const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          data-testid="back-to-top-btn"
          initial={{ opacity: 0, y: 16, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.9 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-50 h-11 w-11 rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/25 flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
        >
          <ArrowUp className="h-5 w-5" strokeWidth={2} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

const FOOTER_COLS = [
  {
    heading: "Engagements",
    links: [
      { label: "JCA Evidence Stress Test", to: "/engagements" },
      { label: "JCA Readiness Assessment", to: "/engagements" },
      { label: "JCA Evidence Investment Strategy", to: "/engagements" },
      { label: "JCA Evidence Governance Partnership", to: "/engagements" },
    ],
  },
  {
    heading: "Explore",
    links: [
      { label: "Who We Serve", to: "/who-we-serve" },
      { label: "Our Approach", to: "/approach" },
      { label: "How Engagements Work", to: "/engagements" },
      { label: "Outcomes", to: "/deliverables" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Contact", to: "/contact" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
    ],
  },
];

export default function SiteLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => { track("pageview", pathname); }, [pathname]);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, restDelta: 0.001 });

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <header className="fixed top-0 inset-x-0 z-50">
        <div className={`bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300 ${scrolled ? "border-slate-200 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)]" : "border-slate-200/60"}`}>
          <div className={`max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-14" : "h-16"}`}>
            <Link to="/" data-testid="nav-logo-home"
              onClick={(e) => { if (pathname === "/") { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
              className="group flex items-center gap-2.5 cursor-pointer">
              <img src="/brand/jca-matrix-logo.png" alt="JCA Matrix™" className="h-8 w-auto transition-transform duration-200 group-hover:scale-[1.02]" />
            </Link>
            <nav className="hidden md:flex items-center gap-7 text-sm">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `relative py-1 transition-colors duration-200 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-full after:origin-left after:bg-blue-600 after:transition-transform after:duration-300 after:ease-out ${isActive ? "text-blue-600 font-medium after:scale-x-100" : "text-slate-600 hover:text-slate-900 after:scale-x-0 hover:after:scale-x-100 hover:after:bg-slate-300"}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <Button data-testid="nav-cta-btn" onClick={() => navigate("/contact")} className="group bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-9 px-4 text-sm font-medium transition-colors duration-200">
              Request a Stress Test <ArrowRight className="ml-1.5 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </div>
          <motion.div className="h-[2px] bg-blue-600 origin-left" style={{ scaleX: progress }} data-testid="scroll-progress-bar" />
        </div>
      </header>

      <Outlet />

      <BackToTop />

      <div className="bg-slate-950 border-b border-slate-800" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 text-center">
          <TrustBadges dark />
        </div>
      </div>

      <footer className="bg-slate-900 text-slate-400" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-5">
            <div className="flex items-center gap-2.5">
              <img src="/brand/jca-matrix-logo-white.png" alt="JCA Matrix™" className="h-8 w-auto" />
            </div>
            <p className="mt-4 text-sm leading-relaxed max-w-sm">
              JCA Matrix™ is a JCA Readiness &amp; Strategy Platform developed by MarksMan Healthcare. It assesses
              EU Joint Clinical Assessment readiness, anticipates likely PICOs and comparators, and turns evidence
              gaps into board-ready strategy, evidence planning and executive deliverables.
            </p>
            <div className="mt-4 font-display text-base font-semibold text-white" data-testid="footer-brandline">
              Not AI. <span className="text-blue-500">Confidence.</span>
            </div>
            <p className="mt-5 text-xs leading-relaxed max-w-sm text-slate-500" data-testid="footer-positioning-note">
              JCA Matrix™ is a strategic decision-support framework designed to strengthen JCA readiness, evidence
              planning and HTA preparedness. It supports — but does not replace — formal regulatory and HTA
              assessment processes.
            </p>
            <Button onClick={() => navigate("/contact")} data-testid="footer-cta-btn"
              className="group mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-10 px-5 text-sm font-medium">
              Request a JCA Evidence Stress Test <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </div>
          {FOOTER_COLS.map((col) => (
            <div key={col.heading} className="md:col-span-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{col.heading}</div>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors duration-200">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="md:col-span-1" />
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} MarksMan Healthcare Communications. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
